import { Button, Icons, Spinner } from "@/components/ui";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesFileMessages } from "@/i18n/messages/frames-file";
import { FileActionButtons, FileNoticeText } from "./file-notice";
import type { PdfReadError } from "./pdf-fetch";
import { PdfCover } from "./pdf-cover";
import type { FileActions } from "./use-file-actions";

/** What the preview says when a PDF cannot be drawn, by cause. */
const ERROR_MESSAGES = {
  refused: "pdfRefused",
  "not-pdf": "pdfNotPdf",
  "too-large": "pdfTooLarge",
  failed: "pdfFailed",
  missing: "fileNotFound",
  unlinked: "notLinkedHere",
  denied: "folderAccessDenied",
} as const satisfies Record<PdfReadError, string>;

/** Causes another read cannot fix: no Try again. */
const FINAL_ERRORS = new Set<PdfReadError>([
  "not-pdf",
  "too-large",
  "unlinked",
]);

/**
 * The preview while its pages are not drawn: the wall card's cover edge to
 * edge at the window's width, with a small spinner while loading, a quiet
 * message by cause with what helps (a web PDF: Open in browser; a linked
 * one: Open, Locate… or Allow in Settings) and, where another read may
 * help, Try again; or, for a file linked on another computer, where it is.
 */
export function PdfPreviewCover({
  thumbnailUrl,
  title,
  ratio,
  width,
  error,
  actions,
  onRetry,
}: {
  thumbnailUrl: string;
  title: string;
  ratio: number;
  /** The window's width, which scales the cover's type. */
  width: number;
  /** Null while the document loads (or is elsewhere). */
  error: PdfReadError | null;
  actions: FileActions;
  onRetry: () => void;
}) {
  const t = useT(framesFileMessages);
  const tc = useT(commonMessages);
  const elsewhere =
    actions.notice?.kind === "elsewhere" ? actions.notice : null;
  const local = actions.pdf?.kind === "local";
  const { onOpen } = actions;
  return (
    <div
      className="relative w-full overflow-hidden"
      style={{
        aspectRatio: ratio,
        // The card's cover type, scaled to the window.
        fontSize: Math.max(13, width / 24),
      }}
      data-testid="file-frame-pdf-cover"
      data-state={error ? "error" : elsewhere ? "elsewhere" : "loading"}
    >
      <PdfCover thumbnailUrl={thumbnailUrl} title={title} />
      {elsewhere ? (
        <div className="absolute inset-x-4 bottom-4 rounded-card bg-[var(--card-chrome)] px-4 py-3.5 text-center text-appkit-body shadow-[var(--card-chrome-shadow)]">
          <FileNoticeText notice={elsewhere} />
        </div>
      ) : error ? (
        <div
          role="status"
          className="absolute inset-x-4 bottom-4 flex flex-col items-center gap-3 rounded-card bg-[var(--card-chrome)] px-4 py-3.5 text-center text-appkit-body text-card-ink shadow-[var(--card-chrome-shadow)]"
          data-testid="file-frame-pdf-error"
          data-error={error}
        >
          <p className="flex items-center gap-2">
            <Icons.Info
              className="h-4 w-4 shrink-0 text-card-muted"
              aria-hidden="true"
            />
            {t(ERROR_MESSAGES[error])}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {local && (
              <FileActionButtons actions={actions} name={title} scale="cover" />
            )}
            {!local && onOpen && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="nodrag"
                onClick={onOpen}
                data-testid="file-frame-pdf-open-browser"
              >
                <Icons.ExternalLink
                  className="h-3.5 w-3.5"
                  aria-hidden="true"
                />
                {t("openInBrowser")}
              </Button>
            )}
            {!FINAL_ERRORS.has(error) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="nodrag"
                onClick={onRetry}
                data-testid="file-frame-pdf-retry"
              >
                <Icons.RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                {tc("retry")}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <span
          className="absolute bottom-3 right-3 grid h-7 w-7 place-items-center rounded-full bg-[var(--card-chrome)] shadow-[var(--card-chrome-shadow)]"
          data-testid="file-frame-pdf-loading"
        >
          <Spinner className="h-4 w-4" />
        </span>
      )}
    </div>
  );
}
